// Shared low-level Server-Sent Events (SSE) reader, used by every `text/event-stream`
// consumer (deal items batches — `stream-deal-items.ts`, deal change signals —
// `useDealEventStream`). Keeps the chunk-buffering / frame-splitting logic in one
// place instead of duplicating it per endpoint.

export type SseFrame = {
  /** The `event:` field, when present. */
  event?: string;
  /** The concatenated `data:` field(s). */
  data: string;
};

/**
 * Parse the fields of a single SSE frame. Returns `null` for a frame carrying
 * neither `data:` nor `event:` (e.g. a `:` comment / keep-alive), which callers
 * should ignore. Multiple `data:` lines are joined with `\n` per the spec.
 */
export function parseSseFrame(frame: string): SseFrame | null {
  let event: string | undefined;
  const dataLines: string[] = [];

  for (const line of frame.split(/\r?\n/)) {
    if (line.startsWith('data:')) {
      const value = line.slice('data:'.length);
      dataLines.push(value.startsWith(' ') ? value.slice(1) : value);
    } else if (line.startsWith('event:')) {
      const value = line.slice('event:'.length);
      event = value.startsWith(' ') ? value.slice(1) : value;
    }
  }

  if (dataLines.length === 0 && event === undefined) return null;
  return { event, data: dataLines.join('\n') };
}

/**
 * Read an SSE response body to completion, invoking `onFrame` for every complete
 * frame. Handles chunk boundaries and CR/LF/CRLF line endings (including a `\r`
 * split across two chunks). The reader is always cancelled on exit.
 *
 * `onFrame` may throw to abort the stream (e.g. on an `event: error` frame); the
 * error propagates to the caller after the reader is cancelled.
 */
export async function readSseFrames(
  body: ReadableStream<Uint8Array>,
  onFrame: (frame: SseFrame) => void
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let pendingCR = false;

  const appendChunk = (text: string): void => {
    let chunk = (pendingCR ? '\r' : '') + text;
    pendingCR = false;
    if (chunk.endsWith('\r')) {
      pendingCR = true;
      chunk = chunk.slice(0, -1);
    }
    chunk = chunk.replace(/\r\n/g, '\n');
    if (!chunk) return;
    const searchStart = Math.max(buffer.length - 1, 0);
    buffer += chunk;
    let separatorIndex = buffer.indexOf('\n\n', searchStart);
    while (separatorIndex !== -1) {
      const frame = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);
      const parsed = parseSseFrame(frame);
      if (parsed) onFrame(parsed);
      separatorIndex = buffer.indexOf('\n\n');
    }
  };

  try {
    let streamDone = false;
    while (!streamDone) {
      const { done, value } = await reader.read();
      streamDone = done;
      if (value) appendChunk(decoder.decode(value, { stream: true }));
    }
    appendChunk(decoder.decode());
  } finally {
    reader.cancel().catch(() => undefined);
  }
}
